import {Box, Typography} from '@mui/material';
import React, {useState} from 'react';
import {useReviewHeaderContext} from '../header/ReviewHeader';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as DownloadIcon} from '../../../../assets/images/download-base-icon.svg';
import {ReactComponent as DeleteIcon} from '../../../../assets/images/delete.svg';

import {EditOutlined} from '@mui/icons-material';
import {t} from 'i18next';
import {getProtocolDownload} from '../../../../api/endpoints/aiReviewApi';
import {createPreciseTimestampedFilename} from '../../../background-tasks/backgroundTasksUtils';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {csvRowsTo2DArray, downloadProtocolXlsx} from './utils';
import ArrModal from '../../../common/arr-modal/ArrModal';

interface IProtocolActionsHeader {
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProtocolActionsHeader = ({
  setIsDeleteModalOpen,
}: IProtocolActionsHeader) => {
  const [showToast, setShowToast] = useState(false);
  const [showEditConfirmationModal, setShowEditConfirmationModal] =
    useState(false);
  const {setShowEditProtocolIntelligence, protocolDetails} =
    useReviewHeaderContext();

  const handleEditProtocolClick = () => {
    if (protocolDetails?.isApproved) setShowEditConfirmationModal(true);
  };

  const onDownloadProtocolClicked = async () => {
    if (!protocolDetails?.protocolId) return;
    const {
      isSuccess = false,
      data: response,
      headers,
    } = await getProtocolDownload(protocolDetails?.protocolId);

    if (response instanceof Blob && isSuccess) {
      const contentType = headers['content-type'] || 'application/octet-stream';

      const fileName = createPreciseTimestampedFilename(
        `Protocol_${protocolDetails?.protocolId}`
      );

      const blob = new Blob([response], {type: contentType});
      const csvText = await blob.text();
      const rowsFromCsv = csvRowsTo2DArray(csvText);
      downloadProtocolXlsx(rowsFromCsv, fileName);
    } else {
      setShowToast(true);
      console.error('download protocol failed');
    }
  };

  const handleProtocolModalClose = () => {
    setShowEditConfirmationModal(false);
  };
  const handleProtocolModalConfirm = () => {
    if (protocolDetails?.isApproved) setShowEditProtocolIntelligence(true);
  };
  return (
    <>
      <ArrModal
        className="edit-protocol-confirmation-modal"
        showModal={showEditConfirmationModal}
        onClose={handleProtocolModalClose}
        showClose={false}
        title={t('ai.review.protocolIntelligence.protocolActions.editProtocol')}
        aria-label={'modalDetails.title'}
        dataTestId="edit-protocol"
        buttons={[
          {
            id: 'cancel-btn',
            label: t('common.cancel'),
            onClick: handleProtocolModalClose,
            variant: 'secondary',
            dataTestId: 'edit-protocol-cancel-button',
          },
          {
            id: 'confirm-btn',
            label: t('common.continue'),
            onClick: handleProtocolModalConfirm,
            variant: 'primary',
            dataTestId: 'edit-protocol-confirm-btn',
          },
        ]}
      >
        <Typography
          className="typo-text body2_size text-body-strong"
          tabIndex={0}
        >
          {t(
            'ai.review.protocolIntelligence.protocolActions.editConfirmationModalTitle'
          )}
        </Typography>
      </ArrModal>

      {showToast && (
        <ArrToastNotification
          message={'Error to download protocol report'}
          onClose={() => {
            setShowToast(false);
          }}
          status={[ToastType.Error]}
          data-testId={`${t(
            'ai.review.protocolIntelligence.protocolActions.downloadProtocol'
          )}-error-toast`}
        />
      )}
      <Box className="protocol-action-section">
        <Typography
          className="typo-text headline3_size text-headings"
          flex={1}
          tabIndex={0}
          data-testid="protocol-actions-header-file-name"
        >
          {protocolDetails?.protocolName}
        </Typography>
        <Box className="actions">
          <ArrIconButton
            size="large"
            icon={<DownloadIcon />}
            onClick={onDownloadProtocolClicked}
            ariaLabel={t(
              'ai.review.protocolIntelligence.protocolActions.downloadProtocol'
            )}
            testId="download-protocol"
          />
          <ArrIconButton
            size="large"
            icon={<EditOutlined />}
            onClick={handleEditProtocolClick}
            ariaLabel={t(
              'ai.review.protocolIntelligence.protocolActions.editProtocol'
            )}
            testId="edit-protocol"
          />
          <ArrIconButton
            size="large"
            icon={<DeleteIcon />}
            onClick={() => setIsDeleteModalOpen(true)}
            ariaLabel={t(
              'ai.review.protocolIntelligence.protocolActions.deleteProtocol'
            )}
            className="delete-icon-btn"
            testId="delete-protocol"
          />
        </Box>
      </Box>
    </>
  );
};
export default ProtocolActionsHeader;
